import L, { LatLngExpression } from 'leaflet'
import { memo, useLayoutEffect, useMemo, useState } from 'react'
import { Marker, Tooltip, useMap } from 'react-leaflet'
import { ICluster } from 'src/generated-types/cluster'
import { getMooringName } from 'src/screens/localities/moorings/positionTableHelper'
import { IMooringType, IPositionType } from '../../../../../generated-types'
import { normalizeBridleConnectedOrder } from '../bridleOrder.action'
import { CreateLayerGroup } from '../ComponentLayer'
import { minZoom } from '../constraints'
import { validatePosition } from '../MapBoundsComponent'
import { midpoint } from '../utils'
import { IProjectInfo } from 'src/generated-types/project'

export const CreateProject: React.FC<{
  project: IProjectInfo
  type?: IPositionType
  mapPosition: L.LatLngExpression
  removeType?: IMooringType
  isFleet?: boolean
  isModalView: boolean
  navigateLocality?: () => void
  clusters?: ICluster[]
  showCageStatusPopOver?: boolean
  onPositionSelect?: (positionId: number, positionName: string) => void
  mooringsLength?: number
}> = memo(
  ({
    type,
    clusters,
    mapPosition,
    removeType = null,
    isFleet,
    isModalView,
    navigateLocality,
    showCageStatusPopOver,
    onPositionSelect,
    mooringsLength,
    project,
  }) => {
    const map = useMap()
    const [viewContent, setViewContent] = useState(map.getZoom() > minZoom ? true : false)
    const center = useMemo(() => {
      const positions = project.positions
      if (positions) {
        const corners = positions.filter(p => p.type == IPositionType.Corner)
        if (corners.length > 2) {
          return midpoint(
            corners[0].latitude,
            corners[0].longitude,
            corners[corners.length - 1].latitude,
            corners[corners.length - 1].longitude,
            0.5,
          ) as LatLngExpression
        }
        if (corners[0]?.latitude && corners[0]?.longitude) {
          return [corners[0]?.latitude, corners[0]?.longitude] as LatLngExpression
        }
      }
      return [0, 0] as LatLngExpression
    }, [project.positions])

    const normalizedPositions = useMemo(
      () => project.positions?.map(p => normalizeBridleConnectedOrder(p, project.type)),
      [project.positions, project.type],
    )

    const isCluster = useMemo(() => {
      if (clusters && clusters?.length > 0) {
        const insideClusterIndex = clusters.findIndex(
          c => c.localities.findIndex(m => m.id == project.localityId) != -1,
        )

        if (insideClusterIndex != -1) {
          return true
        }
      }

      return false
    }, [clusters, project])

    useLayoutEffect(() => {
      if (map.getZoom() < minZoom) {
        setViewContent(false)
      } else {
        setViewContent(true)
      }
      map.addEventListener('zoom', () => {
        if (map.getZoom() < minZoom) {
          setViewContent(false)
        } else {
          setViewContent(true)
        }
      })
      return () => {
        map.removeEventListener('zoom')
      }
    }, [mooringsLength, map])

    const circleEvent = useMemo(() => {
      return {
        click() {
          if (navigateLocality) {
            navigateLocality()
          } else {
            map.fitBounds(
              (project?.positions?.filter(x => validatePosition(x)) || []).map(p => [p.latitude, p.longitude]),
              {
                animate: false,
                duration: 100,
                padding: [20, 20],
                maxZoom: 16,
              },
            )
          }
        },
      }
    }, [navigateLocality, map, project.positions])

    if ((removeType && project.type == removeType) || isCluster == true) {
      return <></>
    }

    if (viewContent) {
      return (
        <CreateLayerGroup
          positions={normalizedPositions}
          type={type}
          mapPosition={mapPosition}
          isFleet={project.type == IMooringType.Barge || isFleet}
          steelFrame={project.type == IMooringType.SteelCage}
          isModalView={isModalView}
          clusters={clusters}
          showCageStatusPopOver={showCageStatusPopOver}
          mooringType={project?.type}
          onPositionSelect={onPositionSelect}
        />
      )
    } else {
      if (center) {
        return (
          <Marker eventHandlers={circleEvent} position={center}>
            <Tooltip direction={'top'} offset={[0, -10]}>
              {getMooringName(project.type as IMooringType)} {project.name ?? 'N/A'}
            </Tooltip>
          </Marker>
        )
      }
    }
    return <></>
  },
)
